import React, { useMemo } from "react";
import {
  Wallet,
  Landmark,
  FileText,
  TrendingUp,
  CalendarDays,
  CalendarRange,
} from "lucide-react";

/**
 * Collection
 * -------------------
 * Modern dashboard view of "Today" and "This month" cash-collection totals,
 * broken down by Cash / Bank transfer / Cheque, with a small donut chart
 * showing the share of each payment method.
 *
 * Usage:
 *   <Collection data={apiResponse} />
 *
 * `data` accepts either the raw array shape returned by the API
 * (e.g. [{ TodayCash: 500, ... }]) or a single object with the same keys.
 *
 * Requires: tailwindcss, lucide-react
 */

const currency = (n) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(Number(n) || 0);

const LINE_ITEMS = [
  {
    key: "Cash",
    label: "Cash",
    icon: Wallet,
    iconBg: "bg-emerald-50",
    iconColor: "text-emerald-600",
    hex: "#10b981",
  },
  {
    key: "BankTransfer",
    label: "Bank transfer",
    icon: Landmark,
    iconBg: "bg-blue-50",
    iconColor: "text-blue-600",
    hex: "#3b82f6",
  },
  {
    key: "Cheque",
    label: "Cheque",
    icon: FileText,
    iconBg: "bg-amber-50",
    iconColor: "text-amber-600",
    hex: "#f59e0b",
  },
];

const SIZE = 112;
const STROKE = 14;
const RADIUS = (SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

function Donut({ segments, total }) {
  const numericTotal = Number(total) || 0;
  const safeTotal = numericTotal > 0 ? numericTotal : 1;

  let cumulative = 0;
  const arcs = segments.map((s) => {
    const value = Number(s.value) || 0;
    const pct = Math.max(0, value / safeTotal);
    const dash = pct * CIRCUMFERENCE;
    const offset = cumulative * CIRCUMFERENCE;
    cumulative += pct;
    return { ...s, value, pct, dash, offset };
  });

  const hasData = numericTotal > 0;
  const dominant = hasData
    ? arcs.reduce((max, a) => (a.pct > max.pct ? a : max), arcs[0])
    : null;

  return (
    <div className="relative shrink-0" style={{ width: SIZE, height: SIZE }}>
      <svg
        width={SIZE}
        height={SIZE}
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        style={{
          transform: "rotate(-90deg)",
          transformOrigin: "50% 50%",
          overflow: "visible",
        }}
      >
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={RADIUS}
          fill="none"
          stroke="#e2e8f0"
          strokeWidth={STROKE}
        />
        {hasData &&
          arcs.map(
            (a) =>
              a.dash > 0 && (
                <circle
                  key={a.key}
                  cx={SIZE / 2}
                  cy={SIZE / 2}
                  r={RADIUS}
                  fill="none"
                  stroke={a.hex}
                  strokeWidth={STROKE}
                  strokeLinecap="round"
                  strokeDasharray={`${Math.max(
                    a.dash - 2,
                    0
                  )} ${CIRCUMFERENCE}`}
                  strokeDashoffset={-a.offset}
                />
              )
          )}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        {dominant ? (
          <>
            <span className="text-[13px] font-bold text-gray-800 leading-none">
              {Math.round(dominant.pct * 100)}%
            </span>
            <span className="text-[8px] font-medium text-gray-400 uppercase tracking-wide mt-0.5">
              {dominant.label}
            </span>
          </>
        ) : (
          <span className="text-[11px] text-gray-300">—</span>
        )}
      </div>
    </div>
  );
}

function Panel({ heading, dateLabel, values, HeadingIcon, accentColor }) {
  const total = values.Total;

  const segments = useMemo(
    () => LINE_ITEMS.map((item) => ({ ...item, value: values[item.key] })),
    [values]
  );

  return (
    <div className="rounded-2xl bg-white border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-200 p-6 sm:p-7">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2.5">
          <div
            className={`w-9 h-9 rounded-xl flex items-center justify-center ${accentColor.bg}`}
          >
            <HeadingIcon className={accentColor.text} size={18} />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900 leading-none">
              {heading}
            </p>
            <p className="text-xs text-gray-400 mt-1">{dateLabel}</p>
          </div>
        </div>
        <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">
          <TrendingUp size={12} />
          Collected
        </span>
      </div>

      {/* Total + donut */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6 gap-4">
        <div>
          <p className="text-3xl sm:text-[34px] font-bold tracking-tight text-gray-900">
            {total}
          </p>
          <p className="text-xs text-gray-400 mt-1">Total collection</p>
        </div>
        <Donut segments={segments} total={total} />
      </div>

      {/* Breakdown */}
      <div className="space-y-3">
        {segments.map((item) => {
          const Icon = item.icon;
          return (
            <div key={item.key} className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div
                  className={`w-6 h-6 rounded-lg flex items-center justify-center ${item.iconBg}`}
                >
                  <Icon className={item.iconColor} size={13} />
                </div>
                <span className="text-sm text-gray-600">{item.label}</span>
              </div>
              <span className="text-sm font-semibold text-gray-900 tabular-nums">
                {item.value}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Collection({ data , type, onClose}) {
  const raw = Array.isArray(data) ? data[0] : data;

  const today = {
    Cash: raw?.TodayCash ?? 0,
    BankTransfer: raw?.TodayBankTransfer ?? 0,
    Cheque: raw?.TodayCheque ?? 0,
    Total: raw?.TodayTotalCollection ?? 0,
  };

  const month = {
    Cash: raw?.MonthCash ?? 0,
    BankTransfer: raw?.MonthBankTransfer ?? 0,
    Cheque: raw?.MonthCheque ?? 0,
    Total: raw?.MonthTotalCollection ?? 0,
  };

  const todayLabel = new Date().toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
  const monthLabel = new Date().toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });

  if (!raw) {
    return (
      <div className="w-full  mx-auto p-10 text-center text-sm text-gray-400">
        No collection data available.
      </div>
    );
  }

  return (
    <div className="w-full  mx-auto py-6 sm:py-8 px-4 sm:px-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {type == "Daily Collection" && (
          <Panel
            heading="Today"
            dateLabel={todayLabel}
            values={today}
            HeadingIcon={CalendarDays}
            accentColor={{ bg: "bg-emerald-50", text: "text-emerald-600" }}
          />
        )}

        {type == "Collection (MTD)" && (
          <Panel
            heading="This month"
            dateLabel={monthLabel}
            values={month}
            HeadingIcon={CalendarRange}
            accentColor={{ bg: "bg-blue-50", text: "text-blue-600" }}
          />
        )}
      </div>

        {/* Sticky Close Bar */}
        <div
        style={{
          position: "sticky",
          bottom: 0,
          zIndex: 10,
          display: "flex",
          justifyContent: "flex-end",
          padding: "8px 4px",
          marginTop: "12px",
          backgroundColor: "#f8fafc",
          borderTop: "1px solid #e2e8f0",
        }}
      >
        <button
          onClick={onClose}
          className="w-full py-3 bg-gradient-to-r from-rose-500 to-pink-500 text-white rounded-xl text-sm font-semibold hover:shadow-lg hover:opacity-95 active:scale-[0.99] transition-all tracking-wider uppercase"
        >
          Close
        </button>
      </div>

    </div>
  );
}
